import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';

// Animation for frog jumping
const jump = keyframes`
  0% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
  100% { transform: translateY(0) rotate(0deg); }
`;

// Fade in animation
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  max-width: 600px;
  margin: 60px auto;
  padding: 40px 20px;
  text-align: center;
  background-color: ${props => props.theme.card};
  border-radius: 12px;
  box-shadow: ${props => props.theme.shadow};
  animation: ${fadeIn} 0.5s ease-out;
  
  @media (max-width: 768px) {
    margin: 40px auto;
    padding: 30px 15px;
  }
  
  @media (max-width: 480px) {
    margin: 20px auto;
    padding: 20px 10px;
  }
`;

const Title = styled.h1`
  color: ${props => props.theme.primary};
  font-size: 36px;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
  
  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

const Subtitle = styled.h2`
  color: ${props => props.theme.text};
  font-size: 20px;
  margin-bottom: 30px;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 25px;
  }
  
  @media (max-width: 480px) {
    font-size: 16px;
    margin-bottom: 20px;
  }
`;

const FrogIcon = styled.div`
  font-size: 100px;
  margin: 20px auto;
  animation: ${jump} 2s infinite ease-in-out;
  display: inline-block;
  
  @media (max-width: 768px) {
    font-size: 80px;
  }
  
  @media (max-width: 480px) {
    font-size: 60px;
  }
`;

const Message = styled.p`
  color: ${props => props.theme.textSecondary};
  margin-bottom: 30px;
  line-height: 1.6;
  font-size: 16px;
  
  @media (max-width: 480px) {
    font-size: 14px;
    margin-bottom: 25px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 30px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 30px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 10px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  @media (max-width: 480px) {
    padding: 10px 20px;
    font-size: 14px;
    width: 80%;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: ${props => props.theme.primary};
  color: white;
  
  &:hover {
    background-color: ${props => props.theme.primaryHover};
  }
`;

const SecondaryButton = styled(Button)`
  background-color: ${props => props.theme.buttonLight};
  color: ${props => props.theme.primary};
  
  &:hover {
    background-color: ${props => props.theme.buttonLightHover};
  }
`;

const NotFound = () => {
  const navigate = useNavigate();
  
  const handleGoHome = () => {
    navigate('/');
  };
  
  const handleContact = () => {
    window.location.href = 'mailto:contactosapotix@gmail.com';
  };
  
  return (
    <Container>
      <FrogIcon role="img" aria-label="Sapito perdido">🐸</FrogIcon>
      <Title>¡Oops! Página no encontrada</Title>
      <Subtitle>Este sapito se ha perdido en el ciberespacio</Subtitle>
      
      <Message>
        La página que estás buscando no existe o ha sido movida.
        <br />
        Mientras tanto, puedes volver a la página principal o contactarnos si necesitas ayuda.
      </Message>
      
      <ButtonContainer>
        <PrimaryButton onClick={handleGoHome}>
          Volver al inicio
        </PrimaryButton>
        <SecondaryButton onClick={handleContact}>
          Contactar soporte
        </SecondaryButton>
      </ButtonContainer>
    </Container>
  );
};

export default NotFound; 